import React from 'react'

import { Box, Link } from '@mui/material'
import NextLink from 'next/link'

import { formatMoney } from 'utils/FormatMoney'

import { ReportFilterEntry } from './index'

const UUID_ROUTES: Record<
  string,
  { href: (uuid: string) => string; label: string }
> = {
  customer_uuid: { href: (u) => `/clientes/${u}`, label: 'Ver cliente' },
  product_uuid: { href: (u) => `/planos/${u}`, label: 'Ver plano' },
  contract_uuid: { href: (u) => `/contratos/${u}`, label: 'Ver contrato' },
  recurrence_uuid: {
    href: (u) =>
      `/cobrancas?tab=recorrencias&recurrence_uuid=${u}&total_cycles=2`,
    label: 'Ver recorrência'
  },
  billing_uuid: {
    href: (u) => `/cobrancas?tab=cobrancas&charge_uuid=${u}&total_cycles=1`,
    label: 'Ver cobrança'
  },
  transfer_uuid: {
    href: (u) => `/cobrancas?tab=transferencias&transfer_uuid=${u}`,
    label: 'Ver transferência'
  },
  group: {
    href: (u) => `/clientes?tab=clientes&group=${u}`,
    label: 'Ver grupo'
  },
  customer_group_uuid: {
    href: (u) => `/clientes?tab=clientes&group=${u}`,
    label: 'Ver grupo'
  }
}

const renderEntryValue = (entry: ReportFilterEntry): React.ReactNode => {
  if (entry.uuid) {
    const route = UUID_ROUTES[entry.key]
    if (!route) return null
    return (
      <NextLink href={route.href(entry.uuid)} passHref legacyBehavior>
        <Link
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          onClick={(e) => e.stopPropagation()}
          sx={{ color: 'inherit', fontWeight: 500 }}
        >
          {route.label}
        </Link>
      </NextLink>
    )
  }

  if (entry.amount !== undefined) return formatMoney(entry.amount)

  return entry.display ?? null
}

export const renderFilters = (parsed?: ReportFilterEntry[]) => {
  if (!parsed?.length) return null

  const nodes = parsed
    .map((entry) => ({ entry, value: renderEntryValue(entry) }))
    .filter(
      ({ entry, value }) => entry.flag || (value !== null && value !== '')
    )

  if (!nodes.length) return null

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexWrap: 'nowrap',
        gap: '0 8px',
        alignItems: 'baseline'
      }}
    >
      {nodes.map(({ entry, value }, i) => (
        <Box component="span" key={entry.key}>
          {i > 0 && (
            <Box component="span" sx={{ mr: '8px', opacity: 0.5 }}>
              ·
            </Box>
          )}
          {entry.flag ? (
            <Box component="span" sx={{ fontWeight: 500 }}>
              {entry.label}
            </Box>
          ) : (
            <>
              <Box component="span" sx={{ fontWeight: 500 }}>
                {entry.label}:
              </Box>{' '}
              <Box component="span">{value}</Box>
            </>
          )}
        </Box>
      ))}
    </Box>
  )
}
